import { call, put, takeLatest } from 'redux-saga/effects'

// redux
import * as authenticationModule from '../modules/authentication'
import * as authApi from '../apiModules/authentication'

// utils
import getToken from '../../utils/getToken'
import {
  setLocalStorageValues,
  clearLocalStorageValues,
  removeFlashMessage,
  removeTempToken,
  updateOrgDetails
} from '../../utils/localStorageManager'

function* sendResetLink({ payload }) {
  const { email } = payload
  const result = yield call(authApi.requestPasswordReset, { email })

  if (result.status === 200) {
    yield put(authenticationModule.sendResetLinkSuccess())
  } else {
    authenticationModule.sendResetLinkFailure()
  }
}

function* sendMagicLink({ payload }) {
  const { email } = payload
  const result = yield call(authApi.requestMagicLink, { email })

  if (result.status === 200) {
    yield put(authenticationModule.sendMagicLinkSuccess())
  } else {
    authenticationModule.sendMagicLinkFailure()
  }
}

function* requestConfirmationEmail({ payload }) {
  const { email } = payload
  const result = yield call(authApi.requestConfirmationEmail, { email })

  if (result.status === 200) {
    yield put(authenticationModule.requestConfirmationEmailSuccess())
  } else {
    authenticationModule.requestConfirmationEmailFailure()
  }
}

function* confirmEmail({ payload }) {
  const { token, navigate } = payload

  const response = yield call(authApi.confirmEmail, { token })
  if (response.data && response.data.justConfirmed) {
    yield setLocalStorageValues(response.data.token, response)
    yield put(authenticationModule.loginSuccess(response.data))

    return navigate('/dashboard')
  } else {
    yield put(authenticationModule.confirmationFailure())
  }
}

function* register({ payload }) {
  const { navigate } = payload
  const response = yield call(authApi.register, payload)

  if (response.data) {
    yield setLocalStorageValues(response.data.token, response)
    yield put(authenticationModule.loginSuccess(response.data))

    // register API response does not include org details for user
    // just re-fetch roles as a way to update it until that is cleaned up
    const authResponse = yield call(authApi.fetchMyRoles)
    yield put(authenticationModule.fetchMyRolesSuccess(authResponse))
    yield updateOrgDetails(authResponse.orgDetails)

    return navigate('/dashboard')
  } else {
    yield put(authenticationModule.loginFailure(response.response.data.errorMessage))
  }
}

function* login({ payload }) {
  const { email, password } = payload

  if (email && password) {
    const loginResponse = yield call(authApi.login, { email, password })

    if (loginResponse.data) {
      yield removeFlashMessage()
      yield setLocalStorageValues(loginResponse.data.token, loginResponse)
      yield put(authenticationModule.loginSuccess(loginResponse.data))

      const { navigate } = payload
      return navigate('/dashboard')
    } else {
      if (loginResponse.response.status === 401) {
        yield put(authenticationModule.loginFailure(loginResponse.message))
      } else {
        yield put(authenticationModule.loginFailure('Please check your connection'))
      }
    }
  } else {
    yield put(authenticationModule.loginFailure('Please fill out the form'))
  }
}

function* generateApiKey() {
  try {
    const response = yield call(authApi.generateApiKey)
    yield put(authenticationModule.generateApiKeySuccess(response.data))
  } catch (e) {
    yield put(authenticationModule.generateApiKeyFailure())
  }
}

function* acceptHostAgreement() {
  try {
    const response = yield call(authApi.acceptHostAgreement)
    yield put(authenticationModule.acceptHostAgreementSuccess(response.data))
  } catch (e) {
    yield put(authenticationModule.acceptHostAgreementFailure())
  }
}

function* attemptAutoLogin({ payload }) {
  const { signInToken } = payload

  if (signInToken && signInToken.length > 0) {
    const loginResponse = yield call(authApi.magicLogin, { signInToken })
    if (loginResponse && loginResponse.data) {
      yield setLocalStorageValues(loginResponse.data.token, loginResponse)
      yield put(authenticationModule.loginSuccess(loginResponse.data))
      window.location.reload()
    }
  }
}

function* processOnboarding({ payload }) {
  try {
    const response = yield call(authApi.processOnboarding, payload)
    yield put(authenticationModule.processOnboardingSuccess(response.data))

    yield removeTempToken()
    yield setLocalStorageValues(response.data.token, response)
    yield put(authenticationModule.loginSuccess(response.data))

    const { navigate } = payload
    return navigate('/dashboard')
  } catch (e) {
    yield put(authenticationModule.processOnboardingFailure())
  }
}

function* resetPassword({ payload }) {
  const { navigate } = payload
  const resetResponse = yield call(authApi.resetPassword, payload)

  if (resetResponse.data) {
    yield removeTempToken()
    yield setLocalStorageValues(resetResponse.data.token, resetResponse)
    yield put(authenticationModule.loginSuccess(resetResponse.data))

    return navigate('/dashboard')
  } else {
    if (resetResponse.response.status === 401) {
      yield put(authenticationModule.resetPasswordFailure(resetResponse.response.data))
    } else {
      const authError = resetResponse.response.data || 'Please check your connection'
      yield put(authenticationModule.loginFailure(authError))
    }
  }
}

function* fetchMyRoles({ payload }) {
  try {
    const token = yield call(getToken)
    const providedToken = payload ? payload.token : null

    if (!token && !providedToken) {
      yield put(authenticationModule.fetchMyRolesFailure())
      return true
    }

    const response = yield call(authApi.fetchMyRoles, payload)
    yield put(authenticationModule.fetchMyRolesSuccess(response))
  } catch (err) {
    yield put(authenticationModule.fetchMyRolesFailure(err))
  }
}

function* logout() {
  const token = yield call(getToken)
  yield call(authApi.logout, token)
  yield clearLocalStorageValues()
  yield put(authenticationModule.logoutSuccess())
  yield window.location.reload()
}

export const authenticationSagas = [
  takeLatest(authenticationModule.REQUEST_CONFIRMATION_EMAIL, requestConfirmationEmail),
  takeLatest(authenticationModule.CONFIRM_EMAIL, confirmEmail),
  takeLatest(authenticationModule.RESET_PASSWORD, resetPassword),
  takeLatest(authenticationModule.SEND_MAGIC_LINK, sendMagicLink),
  takeLatest(authenticationModule.SEND_RESET_LINK, sendResetLink),
  takeLatest(authenticationModule.FETCH_MY_ROLES, fetchMyRoles),
  takeLatest(authenticationModule.LOGIN, login),
  takeLatest(authenticationModule.ATTEMPT_AUTO_LOGIN, attemptAutoLogin),
  takeLatest(authenticationModule.PROCESS_ONBOARDING, processOnboarding),
  takeLatest(authenticationModule.REGISTER, register),
  takeLatest(authenticationModule.GENERATE_API_KEY, generateApiKey),
  takeLatest(authenticationModule.ACCEPT_HOST_AGREEMENT, acceptHostAgreement),
  takeLatest(authenticationModule.LOGOUT, logout)
]
